import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useMemo, useRef, useState, } from 'react';
import clsx from 'clsx';
import { checkDefaultPrevented, combineClasses, combineProps, logInDev, omit, styled, useEventCallback, useEver, useForkRef, useTheme, useThemeProps, } from '../../../foundation';
import { RcMenu } from '../../Menu/Menu';
import { RcButtonDefaultVariant, RcButtonDefaultSize, RcButtonDefaultColor, RcButton, } from '../Button';
import { RcButtonGroup } from '../ButtonGroup';
import { splitButtonStyle, StyledArrowIcon } from './styles';
import { getVariant, RcSplitButtonClasses, RcSplitButtonTouchRippleClasses, variantIsHandler, } from './utils';
var DEFAULT_DELAY = 166;
var _RcSplitButton = forwardRef(function (inProps, ref) {
    var _a;
    var _b, _c;
    var props = useThemeProps({ props: inProps, name: 'RcSplitButton' });
    var classesProp = props.classes, type = props.type, size = props.size, color = props.color, variant = props.variant, disabled = props.disabled, ControlButtonPropsProp = props.ControlButtonProps, ActionButtonPropsProp = props.ActionButtonProps, MenuProps = props.MenuProps, childrenProp = props.children, onOpen = props.onOpen, onClose = props.onClose, className = props.className, loading = props.loading, rest = __rest(props, ["classes", "type", "size", "color", "variant", "disabled", "ControlButtonProps", "ActionButtonProps", "MenuProps", "children", "onOpen", "onClose", "className", "loading"]);
    var theme = useTheme();
    var _d = ActionButtonPropsProp, actionButtonTitle = _d.title, actionButtonClassNameProp = _d.className, ActionButtonProps = __rest(_d, ["title", "className"]);
    var variantIs = variantIsHandler(variant);
    var toButtonSize = ['xlarge', 'xxlarge', 'xxxlarge'].includes(size)
        ? 'large'
        : size;
    var _e = ControlButtonPropsProp, ArrowIconProps = _e.ArrowIconProps, onControlButtonClick = _e.onClick, controlButtonClassNameProp = _e.className, ControlButtonProps = __rest(_e, ["ArrowIconProps", "onClick", "className"]);
    var _f = __read(useState(), 2), anchorEl = _f[0], setAnchorEl = _f[1];
    var _g = __read(useState(DEFAULT_DELAY), 2), enterNextDelay = _g[0], setEnterNextDelay = _g[1];
    var innerRef = useRef(null);
    var splitRef = useForkRef(innerRef, ref);
    var isMenuOpen = !!anchorEl;
    var isMenuEverOpen = useEver(isMenuOpen);
    var _h = useMemo(function () {
        var result = combineClasses(RcSplitButtonClasses, classesProp);
        return {
            classes: omit(result, ['actionButton', 'controlButton', 'menuOpen']),
            actionButtonClassName: result === null || result === void 0 ? void 0 : result.actionButton,
            controlButtonClassName: result === null || result === void 0 ? void 0 : result.controlButton,
        };
    }, [classesProp]), classes = _h.classes, actionButtonClassName = _h.actionButtonClassName, controlButtonClassName = _h.controlButtonClassName;
    var handleClick = useEventCallback(checkDefaultPrevented(function () {
        setAnchorEl(innerRef.current);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }));
    var handleClose = useEventCallback(checkDefaultPrevented(function (event, reason) {
        var _a;
        if (enterNextDelay === 0) {
            setEnterNextDelay(DEFAULT_DELAY);
        }
        setAnchorEl(null);
        (_a = MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.onClose) === null || _a === void 0 ? void 0 : _a.call(MenuProps, event, reason);
        onClose === null || onClose === void 0 ? void 0 : onClose(event, reason);
    }));
    var SharedButtonProps = useMemo(function () {
        return {
            type: type,
            // when give size more than button accept, just set large
            size: toButtonSize,
            color: color,
            variant: getVariant(variant),
            disabled: loading ? true : undefined,
            disabledVariant: loading ? 'mask' : undefined,
        };
    }, [color, loading, toButtonSize, type, variant]);
    var _j = useMemo(function () {
        if (process.env.NODE_ENV !== 'production' &&
            !(childrenProp instanceof Array)) {
            logInDev({
                component: 'RcSplitButton',
                message: 'children must be implement with RcSplitButtonChildrenProps[]',
            });
            return {};
        }
        var _children = [];
        var actionButtonProps = {};
        React.Children.forEach(childrenProp, function (child, i) {
            var _a = child.props, onClick = _a.onClick, children = _a.children, _b = _a.useRcTooltip, useRcTooltip = _b === void 0 ? true : _b, TooltipProps = _a.TooltipProps, title = _a.title;
            if (i === 0) {
                actionButtonProps = {
                    title: actionButtonTitle || title,
                    useRcTooltip: useRcTooltip,
                    TooltipProps: TooltipProps,
                    onClick: onClick,
                    children: children,
                    className: combineClasses(actionButtonClassName, actionButtonClassNameProp),
                };
                return;
            }
            _children.push(React.cloneElement(child, __assign(__assign(__assign({}, (i === 1
                ? {
                    TooltipProps: {
                        // * that provide when menu open that tooltip position issue, value calculate from Mui
                        // * https://github.com/mui-org/material-ui/blob/6f56be2a07185be06984476aed9f518cbbd6b87d/packages/material-ui/src/Grow/Grow.js#L88
                        enterNextDelay: enterNextDelay,
                        onOpen: function () {
                            if (enterNextDelay === DEFAULT_DELAY) {
                                setEnterNextDelay(0);
                            }
                        },
                    },
                }
                : {})), child.props), combineProps({
                onClick: handleClose,
            }, {
                // run outside method first, if outside prevent default, that will not close menu
                onClick: onClick,
            }))));
        });
        return {
            actionButtonProps: actionButtonProps,
            children: _children,
        };
    }, [
        actionButtonClassName,
        actionButtonClassNameProp,
        actionButtonTitle,
        childrenProp,
        enterNextDelay,
        handleClose,
    ]), actionButtonProps = _j.actionButtonProps, children = _j.children;
    var TouchRippleProps = useMemo(function () {
        return variant === 'round'
            ? {
                classes: RcSplitButtonTouchRippleClasses,
            }
            : {};
    }, [variant]);
    var _ActionButtonProps = useMemo(function () {
        return __assign(__assign(__assign({ TouchRippleProps: TouchRippleProps,
            loading: loading }, actionButtonProps), SharedButtonProps), ActionButtonProps);
    }, [
        ActionButtonProps,
        SharedButtonProps,
        TouchRippleProps,
        actionButtonProps,
        loading,
    ]);
    var _ControlButtonProps = useMemo(function () {
        var combineControlProps = combineProps({
            onClick: handleClick,
            TouchRippleProps: TouchRippleProps,
            className: controlButtonClassName,
        }, {
            onClick: onControlButtonClick,
            className: controlButtonClassNameProp,
        });
        return __assign(__assign(__assign({ useRcTooltip: true }, SharedButtonProps), combineControlProps), ControlButtonProps);
    }, [
        handleClick,
        controlButtonClassName,
        onControlButtonClick,
        TouchRippleProps,
        controlButtonClassNameProp,
        SharedButtonProps,
        ControlButtonProps,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(RcButtonGroup, __assign({ disableRipple: ((_c = (_b = theme.props) === null || _b === void 0 ? void 0 : _b.MuiButtonGroup) === null || _c === void 0 ? void 0 : _c.disableRipple) ||
                variantIs(['plain', 'plainIcon']) }, rest, { className: clsx(className, (_a = {},
                _a[RcSplitButtonClasses.menuOpen] = isMenuOpen,
                _a)), innerRef: splitRef, classes: classes }),
            React.createElement(RcButton, __assign({}, _ActionButtonProps, { focusVariant: "ripple" })),
            React.createElement(RcButton, __assign({ "aria-haspopup": "listbox" }, _ControlButtonProps, { focusVariant: "ripple" }),
                React.createElement(StyledArrowIcon, __assign({ open: isMenuOpen, size: variantIs(['round', 'plainIcon']) ? size : toButtonSize }, ArrowIconProps)))),
        isMenuEverOpen && (React.createElement(RcMenu, __assign({}, MenuProps, { anchorEl: anchorEl, open: isMenuOpen, onClose: handleClose }), children))));
});
var RcSplitButton = styled(_RcSplitButton)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), splitButtonStyle);
RcSplitButton.displayName = 'RcSplitButton';
RcSplitButton.defaultProps = {
    size: RcButtonDefaultSize,
    color: RcButtonDefaultColor,
    variant: RcButtonDefaultVariant,
    ControlButtonProps: {},
    ActionButtonProps: {},
    MenuProps: {},
    disableElevation: true,
};
export { RcSplitButton };
var templateObject_1;
